import React, { useCallback, useState } from 'react';
import {
  Box,
  DownloadIcon,
  NotificationType,
  Notification,
  LoadingSpinner
} from 'edenred-ui';
import { Trans, useTranslation } from 'react-i18next';
import { DropdownOptionAPI } from '@ingestro/importer-react';

import { routerPaths } from '@epi-constants/routerPaths';
import { useGetBenefitGroupsQuery } from '@epi-api/benefitGroups';
import {
  useCancelImportMutation,
  useGetImportStatusQuery
} from '@epi-api/massUpload/massUpload';
import { useGetDetailsQuery } from '@epi-api/user';

import { IngestroImporter, IngestroError } from './components';
import {
  ImportEmployeesContent,
  ImportEmployeesPanel,
  IngestroWrapper,
  SimpleLink
} from './ImportEmployees.styles';
import { useImportEmployees } from './useImportEmployees';
import { useLeaveImportEmployeesConfirmation } from './useLeaveImportEmployeesConfirmation';
import { AnotherUploadInProgress } from './components/AnotherUploadInProgress';
import { LeaveImportEmployeesModal } from './components/LeaveImportEmployeesModal';
import { FileFormatDropdown } from '../../shared/FileFormatDropdown';

export const ImportEmployees = () => {
  const { data: groupSettings } = useGetBenefitGroupsQuery();
  const [cancelImport] = useCancelImportMutation();
  const { t } = useTranslation();
  const [requestId, setRequestId] = useState<number | null>(null);
  const [ingestroError, setIngestroError] = useState<Error | null>(null);
  const [uploadInProgress, setUploadInProgress] = useState(false);
  const { data: importStatus } = useGetImportStatusQuery();
  const { data: customerData, isLoading: isLoadingCustomerData } =
    useGetDetailsQuery();
  const hasEmployees = customerData?.isEmployeesSet;
  useImportEmployees({
    ingestroError,
    setIngestroError
  });
  const isAnotherUpload =
    importStatus?.isAnyImportInProgress && !isLoadingCustomerData;

  const handleLeave = useCallback(() => {
    if (uploadInProgress && requestId) {
      cancelImport({ requestId });
      setUploadInProgress(false);
    }
  }, [uploadInProgress, requestId, cancelImport]);

  const shouldConfirmLeave = !(
    importStatus?.isAnyImportInProgress && !isLoadingCustomerData
  );

  const { isOpen, confirmLeave, cancelLeave, allowNextNavigation } =
    useLeaveImportEmployeesConfirmation(shouldConfirmLeave, handleLeave);

  const groups = groupSettings?.map(group => ({
    label: group.groupName,
    value: group.groupName,
    alternativeMatches: [],
    type: 'string' as const
  })) as DropdownOptionAPI[];

  return (
    <ImportEmployeesPanel
      hasOverflow={false}
      title={t('containers.beneficiaries.import_employees.title')}
      backIcon
      backIconLink={routerPaths.beneficiaries}
      headerContent={
        ingestroError || isAnotherUpload ? null : (
          <FileFormatDropdown
            buttonId="download-template-button"
            buttonText={t(
              'containers.beneficiaries.import_employees.download_button'
            )}
            icon={<DownloadIcon />}
            formatButtonIdPrefix="file-format-button"
          />
        )
      }
      footer={
        uploadInProgress && requestId
          ? {
              hideSubmitButton: true,
              backText: t(
                'containers.beneficiaries.import_employees.in_progress.cancel'
              ),
              onBack: () => {
                cancelImport({ requestId });
                setUploadInProgress(false);
              }
            }
          : undefined
      }
    >
      {ingestroError && <IngestroError />}
      {isLoadingCustomerData && <LoadingSpinner />}
      {isAnotherUpload ? (
        <AnotherUploadInProgress hasEmployees={hasEmployees} />
      ) : (
        <ImportEmployeesContent
          display={
            ingestroError || importStatus?.isAnyImportInProgress
              ? 'none'
              : 'flex'
          }
        >
          <Box pb={6}>
            <Notification
              id="import-employees-info"
              type={NotificationType.Info}
              text={
                <Trans
                  i18nKey={
                    uploadInProgress
                      ? t(
                          'containers.beneficiaries.import_employees.in_progress.notification'
                        )
                      : t('containers.beneficiaries.import_employees.info')
                  }
                  components={{
                    a: (
                      <SimpleLink
                        target="_blank"
                        rel="noreferrer"
                        href={t(
                          'containers.beneficiaries.import_employees.info_url'
                        )}
                      />
                    )
                  }}
                />
              }
            />
          </Box>
          <IngestroWrapper className="import-employees-wrapper">
            {groups ? (
              <IngestroImporter
                groups={groups}
                uploadInProgress={uploadInProgress}
                setUploadInProgress={setUploadInProgress}
                requestId={requestId}
                setRequestId={setRequestId}
                allowNextNavigation={allowNextNavigation}
              />
            ) : (
              <LoadingSpinner />
            )}
          </IngestroWrapper>
        </ImportEmployeesContent>
      )}
      <LeaveImportEmployeesModal
        isOpen={isOpen}
        onStay={cancelLeave}
        onLeave={confirmLeave}
      />
    </ImportEmployeesPanel>
  );
};
